import {useCallback, useEffect, useState} from 'react'
import {useQuery} from "@tanstack/react-query";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {useActiveMarker, useMarkerStore} from "../../../../store/markerStore";
import {DownloadInfoItem, PREPROCESSED_ANALYTICS} from "../../../../types/analytics";
import Api from "../../../../api/Api";
import {userGroupValidation} from "../../../../utils/userGroupValidation";
import {DataAccess, SubscriptionType, WaterdataGroup} from "../../../../types/userGroupPermissions";
import {Moment} from "moment";
import {isEmpty} from "underscore";
import {QUERY_KEYS} from "../../../../queries/queryKeys";
import {DownloadCheckboxKey} from "../../../../utils/common";

export const useDatasetMetadata = (
  namePanel: string,
  coords: number[],
  item: DownloadInfoItem,
  onDateChange: (dateRange: [Moment, Moment], defaultPanel: boolean) => void,
  handleChangePanelCategory: (key: string) => void
) => {
  const user = useAuthStore(state => state.user);
  const setUserGroupPermissions = useAuthStore(state => state.setUserGroupPermissions);
  const overridePointData = useHomeStore(state => state.overridePointData);

  const point = useActiveMarker();
  const setDatasetOffline = useMarkerStore(state => state.setDatasetOffline);
  const setDatasetReport = useMarkerStore(state => state.setDatasetReport);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isTNSDatasetWarningVisible, setIsTNSDatasetWarningVisible] = useState(false);
  const [hasDatasets, setHasDatasets] = useState(false);
  const [datasetIsOffline, setDatasetIsOffline] = useState(false)

  const metadataQuery = useQuery({
    queryKey: [QUERY_KEYS.dataset.metadata, item?.datasetId],
    queryFn: async () => {
      if (!item?.datasetId) return null;
      return await Api.metadata.getMetadata(item.datasetId);
    },
    enabled: !!item?.datasetId
  });

  const directDownloadQuery = useQuery({
    queryKey: [QUERY_KEYS.order.directDownload, item?.datasetId],
    queryFn: async () => {
      if (!item?.datasetId) return null;
      return await Api.moodV2Order.checkDirectDownload(item.datasetId);
    },
    enabled: !!item?.datasetId
  });

  useEffect(() => {
    if (!metadataQuery.data || !point || !item) return;

    const metaData = metadataQuery.data;
    const isOffline = !!(metaData && metaData.IsOffline);
    setDatasetIsOffline(isOffline);

    const analyticDefs = metaData && metaData.AnalyticsDefinition
      ? metaData.AnalyticsDefinition.split('|')
      : [];

    const includesPreprocessed = analyticDefs.find(
      (analytic: any) => analytic === PREPROCESSED_ANALYTICS.XLS || analytic === 'XLS'
    );

    setDatasetOffline(point.id, namePanel, isOffline);
    setDatasetReport(point.id, namePanel, includesPreprocessed !== undefined);

    if (metaData && metaData.TimeDefinition) {
      const permissions = userGroupValidation(
        user?.waterdataGroup
          ? WaterdataGroup[user.waterdataGroup as WaterdataGroup]
          : null,
        user?.subscriptionType
          ? SubscriptionType[user.subscriptionType as SubscriptionType]
          : null,
        metaData.TimeDefinition,
        user?.confidentialDatasetIds
      );

      setUserGroupPermissions((prevState) => {
        const currentValues = prevState[point.id] || {};
        return {
          ...prevState,
          [point.id]: {
            ...currentValues,
            [namePanel]: permissions
          }
        };
      });
      if (permissions?.dataAccess === DataAccess.download) {
        setShowDownloadButton(permissions.dataAccess === DataAccess.download);
      }

      if (permissions?.dateRange) {
        onDateChange(permissions.dateRange, true);
      } else if(item?.dateRange) {
        onDateChange(item.dateRange, true);
      }
    }
  }, [metadataQuery.data, overridePointData, user, item.datasetId]);

  useEffect(() => {
    if (directDownloadQuery.data) {
      setShowDownloadButton(!!directDownloadQuery.data?.Result);
    }
  }, [directDownloadQuery.data, setShowDownloadButton]);

  useEffect(() => {
    if (item.datasetId && item.datasetId && coords) {
      showTNSWarning(item.datasetId);

      initializeActivePanel();
    }
  }, [item?.datasetId, coords]);

  useEffect(() => {
    setHasDatasets(item.options.length > 0);
  }, [item?.options]);

  const showTNSWarning = useCallback((datasetId: string) => {
    if (!datasetId) return;

    const tns = [''];
    if (tns.includes(datasetId)) {
      let showWarning = true;
      const warningSeenDay = localStorage.getItem('tns');
      localStorage.setItem('tns', Date.now().toString());
      if (warningSeenDay) {
        const lastseenDate = Number(warningSeenDay);
        if (!isNaN(lastseenDate)) {
          const secondsPast = Date.now() - lastseenDate / 1000;
          showWarning = false;
          if (secondsPast > 86400) {
            showWarning = false;
          }
        }
      }
      setIsTNSDatasetWarningVisible(showWarning);
    }
  }, [setIsTNSDatasetWarningVisible]);

  const onShowMetadata = useCallback((datasetId: string, fn: (id: string) => void) => {
    if (isEmpty(datasetId)) {
      return;
    }
    fn(datasetId);
  }, []);

  const initializeActivePanel = useCallback(() => {
    if (point && point.activeDatasetKey === undefined) {
      handleChangePanelCategory(DownloadCheckboxKey.SW);
    }
  }, [point, handleChangePanelCategory]);

  return {
    isTNSDatasetWarningVisible,
    setIsTNSDatasetWarningVisible,
    showDownloadButton,
    setShowDownloadButton,
    hasDatasets,
    datasetIsOffline,
    onShowMetadata,
  };
}
